import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ArticleContent from "../blog/ArticleContent";
import contentApi from "../../services/contentApi";
import {
  blocksToMarkdown,
  createBlock,
  markdownToBlocks,
  videoEmbedUrl,
} from "./blogBlockUtils";
import {
  formatInlineSelection,
  linkInlineSelection,
} from "./blogInlineFormatting";
import "./BlogEditor.css";

const EMPTY_FORM = {
  title: "",
  slug: "",
  excerpt: "",
  category: "",
  tags: "",
  cover_image_url: "",
  cover_image_alt: "",
  cover_image_caption: "",
  is_featured: false,
  published_at: "",
  seo_title: "",
  meta_description: "",
};

const BLOCK_TYPES = [
  { type: "paragraph", label: "Paragraph", icon: "paragraph", hint: "Start writing" },
  { type: "heading", label: "Heading", icon: "heading", hint: "Organise the story" },
  { type: "list", label: "List", icon: "list-ul", hint: "Share clear steps" },
  { type: "quote", label: "Quote", icon: "quote-left", hint: "Highlight a thought" },
  { type: "image", label: "Image", icon: "image", hint: "Upload or link an image" },
  { type: "video", label: "Video", icon: "circle-play", hint: "Embed YouTube or Vimeo" },
  { type: "divider", label: "Divider", icon: "minus", hint: "Separate sections" },
];

const BLOG_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const BLOG_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const BLOG_IMAGE_WIDTHS = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "full", label: "Full width" },
];
const BLOG_IMAGE_ALIGNMENTS = [
  { value: "left", label: "Align left", icon: "align-left" },
  { value: "center", label: "Align centre", icon: "align-center" },
  { value: "right", label: "Align right", icon: "align-right" },
];

const imageUploadValidationMessage = (file) => {
  if (!BLOG_IMAGE_TYPES.has(file?.type)) return "Choose a JPEG, PNG, WebP, or GIF image.";
  if (file.size > BLOG_IMAGE_MAX_BYTES) return "Images must be 5 MiB or smaller.";
  return "";
};

const PUBLISHING_OPTIONS = [
  { value: "draft", label: "Draft", icon: "file-lines" },
  { value: "now", label: "Publish now", icon: "paper-plane" },
  { value: "schedule", label: "Schedule", icon: "calendar-day" },
  { value: "archived", label: "Archive", icon: "box-archive" },
];

const INLINE_FORMAT_OPTIONS = [
  { value: "bold", label: "Bold", icon: "bold", shortcut: "Ctrl+B" },
  { value: "italic", label: "Italic", icon: "italic", shortcut: "Ctrl+I" },
  { value: "underline", label: "Underline", icon: "underline", shortcut: "Ctrl+U" },
  { value: "strikethrough", label: "Strikethrough", icon: "strikethrough" },
  { value: "code", label: "Inline code", icon: "code" },
];

const TEXT_BLOCK_TYPES = new Set(["paragraph", "heading", "list", "quote"]);

const cleanText = (value) => String(value || "").trim();

const normalizeTags = (value) => {
  const seen = new Set();
  return String(value || "")
    .split(/[,\n]/)
    .map((tag) => tag.trim())
    .filter((tag) => {
      const key = tag.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const toDateTimeLocal = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const formForPost = (post) => ({
  ...EMPTY_FORM,
  title: post?.title || "",
  slug: post?.slug || "",
  excerpt: post?.excerpt || "",
  category: post?.category || "",
  tags: Array.isArray(post?.tags) ? post.tags.join(", ") : "",
  cover_image_url: post?.cover_image_url || "",
  cover_image_alt: post?.cover_image_alt || "",
  cover_image_caption: post?.cover_image_caption || "",
  is_featured: Boolean(post?.is_featured),
  published_at: toDateTimeLocal(post?.published_at),
  seo_title: post?.seo_title || "",
  meta_description: post?.meta_description || "",
});

const publishingModeFor = (post) => {
  if (!post || post.status === "draft") return "draft";
  if (post.status === "archived") return "archived";
  const publishedAt = new Date(post.published_at);
  return post.published_at && publishedAt.getTime() > Date.now() ? "schedule" : "now";
};

const publishingFields = (form, mode) => {
  if (mode === "draft") return { status: "draft", published_at: null };
  if (mode === "archived") {
    const archivedAt = new Date(form.published_at);
    return {
      status: "archived",
      published_at:
        form.published_at && !Number.isNaN(archivedAt.getTime())
          ? archivedAt.toISOString()
          : null,
    };
  }
  if (mode === "schedule") {
    return { status: "published", published_at: new Date(form.published_at).toISOString() };
  }
  const existingDate = new Date(form.published_at);
  return {
    status: "published",
    published_at:
      form.published_at &&
      !Number.isNaN(existingDate.getTime()) &&
      existingDate.getTime() <= Date.now()
        ? existingDate.toISOString()
        : new Date().toISOString(),
  };
};

const editorValidationError = (message, field, panel = "document") => ({
  message,
  field,
  panel,
});

const validateEditor = (form, blocks, mode) => {
  const content = blocksToMarkdown(blocks);
  if (!cleanText(form.title)) {
    return editorValidationError("Add a title before saving this story.", "blog-editor-title");
  }
  if (!cleanText(form.excerpt)) {
    return editorValidationError(
      "Add a short excerpt for article cards and search results.",
      "blog-editor-excerpt"
    );
  }
  if (!cleanText(content)) {
    return editorValidationError(
      "Add at least one content block before saving.",
      "blog-editor-content"
    );
  }
  const invalidImageIndex = blocks.findIndex(
    (block) => block.type === "image" && cleanText(block.src) && !cleanText(block.alt)
  );
  if (invalidImageIndex >= 0) {
    return editorValidationError(
      `Describe the image in block ${invalidImageIndex + 1} for screen-reader users.`,
      `blog-editor-image-alt-${blocks[invalidImageIndex].id}`
    );
  }
  const invalidVideoIndex = blocks.findIndex(
    (block) => block.type === "video" && cleanText(block.url) && !videoEmbedUrl(block.url)
  );
  if (invalidVideoIndex >= 0) {
    return editorValidationError(
      `Use a complete HTTPS YouTube or Vimeo link in video block ${invalidVideoIndex + 1}.`,
      `blog-editor-block-${blocks[invalidVideoIndex].id}`
    );
  }
  if (!cleanText(form.category)) {
    return editorValidationError(
      "Choose or enter a category for this story.",
      "blog-editor-category",
      "post"
    );
  }
  if (!cleanText(form.cover_image_url)) {
    return editorValidationError(
      "Add a cover image URL in Post settings.",
      "blog-editor-cover-image-url",
      "post"
    );
  }
  if (!cleanText(form.cover_image_alt)) {
    return editorValidationError(
      "Describe the cover image for screen-reader users.",
      "blog-editor-cover-image-alt",
      "post"
    );
  }
  if (normalizeTags(form.tags).length > 12) {
    return editorValidationError(
      "Use no more than 12 tags.",
      "blog-editor-tags",
      "post"
    );
  }
  if (mode === "schedule") {
    const scheduledAt = new Date(form.published_at);
    if (!form.published_at || Number.isNaN(scheduledAt.getTime())) {
      return editorValidationError(
        "Choose a valid publication date and time.",
        "blog-editor-publication-date",
        "post"
      );
    }
    if (scheduledAt.getTime() <= Date.now()) {
      return editorValidationError(
        "Scheduled publication must be set to a future date and time.",
        "blog-editor-publication-date",
        "post"
      );
    }
  }
  return null;
};

const payloadFor = (form, blocks, mode) => {
  const payload = {
    title: cleanText(form.title),
    excerpt: cleanText(form.excerpt),
    content: blocksToMarkdown(blocks),
    category: cleanText(form.category),
    tags: normalizeTags(form.tags),
    cover_image_url: cleanText(form.cover_image_url),
    cover_image_alt: cleanText(form.cover_image_alt),
    cover_image_caption: cleanText(form.cover_image_caption),
    is_featured: Boolean(form.is_featured),
    seo_title: cleanText(form.seo_title),
    meta_description: cleanText(form.meta_description),
    ...publishingFields(form, mode),
  };
  const slug = cleanText(form.slug);
  if (slug) payload.slug = slug;
  return payload;
};

const primaryActionLabel = (mode, saving, saveIntent) => {
  if (saving) {
    if (saveIntent === "now") return "Publishing...";
    if (saveIntent === "schedule") return "Scheduling...";
    if (saveIntent === "archived") return "Archiving...";
    return "Saving draft...";
  }
  if (mode === "schedule") return "Schedule";
  if (mode === "archived") return "Archive";
  if (mode === "draft") return "Save draft";
  return "Publish";
};

const CharacterCount = ({ value, max }) => (
  <small className="blog-editor-character-count">
    {String(value || "").length}/{max}
  </small>
);

const EditorNotice = ({ notice, onDismiss }) => (
  <div
    className={`blog-editor-notice blog-editor-notice--${notice.type}`}
    role={notice.type === "error" ? "alert" : "status"}
    aria-live="polite"
  >
    <i
      className={`fas fa-${notice.type === "error" ? "circle-exclamation" : "circle-check"}`}
      aria-hidden="true"
    />
    <span>{notice.message}</span>
    <button type="button" onClick={onDismiss} aria-label="Dismiss message">
      <i className="fas fa-xmark" aria-hidden="true" />
    </button>
  </div>
);

const SaveActionIcon = ({ saving }) => (
  <i
    className={`fas fa-${saving ? "spinner fa-spin" : "floppy-disk"}`}
    aria-hidden="true"
  />
);

const BlockInserter = ({ onInsert }) => (
  <aside className="blog-editor-inserter" aria-label="Add content blocks">
    <div className="blog-editor-inserter__heading">
      <span className="blog-editor-inserter__mark" aria-hidden="true">
        <i className="fas fa-plus" />
      </span>
      <div>
        <strong>Add a block</strong>
        <span>Build your story section by section.</span>
      </div>
    </div>
    <div className="blog-editor-inserter__grid">
      {BLOCK_TYPES.map((item) => (
        <button type="button" key={item.type} onClick={() => onInsert(item.type)}>
          <span aria-hidden="true"><i className={`fas fa-${item.icon}`} /></span>
          <strong>{item.label}</strong>
          <small>{item.hint}</small>
        </button>
      ))}
    </div>
  </aside>
);

const BlockToolbar = ({ block, index, total, locked, onMove, onRemove }) => {
  const type = BLOCK_TYPES.find((item) => item.type === block.type) || BLOCK_TYPES[0];
  return (
    <header className="blog-editor-block__toolbar">
      <span><i className={`fas fa-${type.icon}`} aria-hidden="true" /> {type.label}</span>
      <div>
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={locked || index === 0}
          aria-label={`Move ${type.label.toLowerCase()} block up`}
          title="Move block up"
        >
          <i className="fas fa-arrow-up" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={locked || index === total - 1}
          aria-label={`Move ${type.label.toLowerCase()} block down`}
          title="Move block down"
        >
          <i className="fas fa-arrow-down" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="is-danger"
          onClick={onRemove}
          disabled={locked}
          aria-label={`Delete ${type.label.toLowerCase()} block`}
          title="Delete block"
        >
          <i className="far fa-trash-can" aria-hidden="true" />
        </button>
      </div>
    </header>
  );
};

const InlineFormattingToolbar = ({ onFormat, onAddLink }) => (
  <div className="blog-editor-inline-formatting" role="toolbar" aria-label="Text formatting">
    <span className="blog-editor-inline-formatting__label">Format</span>
    {INLINE_FORMAT_OPTIONS.map((option) => (
      <button
        type="button"
        key={option.value}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => onFormat(option.value)}
        aria-label={option.label}
        aria-keyshortcuts={option.shortcut ? option.shortcut.replace("Ctrl", "Control") : undefined}
        title={`${option.label}${option.shortcut ? ` (${option.shortcut})` : ""}`}
      >
        <i className={`fas fa-${option.icon}`} aria-hidden="true" />
      </button>
    ))}
    <span className="blog-editor-inline-formatting__separator" aria-hidden="true" />
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onAddLink}
      aria-label="Add link"
      aria-keyshortcuts="Control+K"
      title="Add link (Ctrl+K)"
    >
      <i className="fas fa-link" aria-hidden="true" />
    </button>
  </div>
);

const EditableBlock = ({
  block,
  index,
  total,
  selected,
  locked,
  token,
  onSelect,
  onChange,
  onUploaded,
  onMove,
  onMoveTo,
  onRemove,
  onStatus,
  onUploadState,
}) => {
  const blockRef = useRef(block);
  blockRef.current = block;
  const update = (changes) => onChange(blockRef.current.id, changes);
  const textInputRef = useRef(null);
  const linkUrlRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const [linkEditor, setLinkEditor] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");
  const linkEditorOpen = Boolean(linkEditor);
  const supportsInlineFormatting = TEXT_BLOCK_TYPES.has(block.type);
  const editableValue = block.type === "list"
    ? (block.items || []).join("\n")
    : String(block.text || "");

  const updateEditableValue = (value) => {
    if (block.type === "list") {
      update({ items: value.split("\n") });
      return;
    }
    update({ text: value });
  };

  const currentSelection = () => {
    const input = textInputRef.current;
    return {
      start: input?.selectionStart ?? editableValue.length,
      end: input?.selectionEnd ?? editableValue.length,
    };
  };

  useEffect(() => {
    const pending = pendingSelectionRef.current;
    const input = textInputRef.current;
    if (!pending || !input) return;
    input.focus();
    input.setSelectionRange(pending.start, pending.end);
    pendingSelectionRef.current = null;
  }, [editableValue]);

  useEffect(() => {
    if (!linkEditorOpen || !linkUrlRef.current) return;
    linkUrlRef.current.focus();
    linkUrlRef.current.select();
  }, [linkEditorOpen]);

  const applyFormat = (formatName) => {
    const selection = currentSelection();
    const formatted = formatInlineSelection(
      editableValue,
      selection.start,
      selection.end,
      formatName
    );
    if (!formatted) return;
    pendingSelectionRef.current = {
      start: formatted.selectionStart,
      end: formatted.selectionEnd,
    };
    updateEditableValue(formatted.value);
  };

  const openLinkEditor = () => {
    const selection = currentSelection();
    setLinkEditor({ ...selection, url: "https://", error: "" });
  };

  const closeLinkEditor = () => {
    const selection = linkEditor;
    setLinkEditor(null);
    window.setTimeout(() => {
      const input = textInputRef.current;
      if (!input || !selection) return;
      input.focus();
      input.setSelectionRange(selection.start, selection.end);
    }, 0);
  };

  const applyLink = (event) => {
    event.preventDefault();
    const selectedText = editableValue.slice(linkEditor?.start, linkEditor?.end);
    if (selectedText.includes("\n")) {
      setLinkEditor((current) => ({
        ...current,
        error: "Add a link to one line or list item at a time.",
      }));
      return;
    }
    const formatted = linkInlineSelection(
      editableValue,
      linkEditor?.start,
      linkEditor?.end,
      linkEditor?.url
    );
    if (!formatted) {
      setLinkEditor((current) => ({
        ...current,
        error: "Enter a complete http, https, or mailto link.",
      }));
      return;
    }
    pendingSelectionRef.current = {
      start: formatted.selectionStart,
      end: formatted.selectionEnd,
    };
    updateEditableValue(formatted.value);
    setLinkEditor(null);
  };

  const handleFormattingShortcut = (event) => {
    if ((!event.ctrlKey && !event.metaKey) || event.altKey) return;
    const key = event.key.toLowerCase();
    const shortcutFormats = { b: "bold", i: "italic", u: "underline" };
    if (shortcutFormats[key]) {
      event.preventDefault();
      applyFormat(shortcutFormats[key]);
    } else if (key === "k") {
      event.preventDefault();
      openLinkEditor();
    }
  };

  const textInputFormattingProps = supportsInlineFormatting
    ? { ref: textInputRef, onKeyDown: handleFormattingShortcut }
    : {};

  const uploadImage = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const fileError = imageUploadValidationMessage(file);
    if (fileError) {
      setImageUploadError(fileError);
      return;
    }

    setImageUploadError("");
    setUploadingImage(true);
    onUploadState?.(true);
    try {
      const uploaded = await contentApi.uploadImage(file, token);
      onUploaded?.(blockRef.current.id, uploaded);
      onStatus?.({ type: "success", message: `${file.name} was uploaded.` });
    } catch (error) {
      const message = error?.status === 401
        ? "Your session expired. Sign in again before uploading an image."
        : error?.status === 403
          ? "This account cannot upload blog images."
          : error?.message || "The image could not be uploaded.";
      setImageUploadError(message);
      onStatus?.({ type: "error", message });
    } finally {
      setUploadingImage(false);
      onUploadState?.(false);
    }
  };

  const startDragging = (event) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", block.id);
  };

  const acceptDrop = (event) => {
    event.preventDefault();
    const draggedId = event.dataTransfer.getData("text/plain");
    if (draggedId && draggedId !== block.id) onMoveTo(draggedId, block.id);
  };

  return (
    <section
      className={`blog-editor-block blog-editor-block--${block.type}${selected ? " is-selected" : ""}`}
      onClick={onSelect}
      onFocus={onSelect}
      onDragOver={(event) => event.preventDefault()}
      onDrop={acceptDrop}
      id={`blog-editor-block-${block.id}`}
      aria-label={`${block.type} content block`}
    >
      <BlockToolbar
        block={block}
        index={index}
        total={total}
        locked={locked}
        onMove={onMove}
        onRemove={onRemove}
      />
      <button
        type="button"
        className="blog-editor-block__drag-handle"
        draggable={!locked}
        onDragStart={startDragging}
        disabled={locked}
        aria-label={`Drag ${block.type} block to reorder`}
        title="Drag to reorder"
      >
        <i className="fas fa-grip-vertical" aria-hidden="true" />
      </button>
      <div className="blog-editor-block__body">
        {supportsInlineFormatting && (
          <>
            <InlineFormattingToolbar onFormat={applyFormat} onAddLink={openLinkEditor} />
            {linkEditor && (
              <form
                className="blog-editor-link-editor"
                aria-label="Insert link"
                onSubmit={applyLink}
              >
                <label>
                  <span>Link URL</span>
                  <input
                    ref={linkUrlRef}
                    type="text"
                    inputMode="url"
                    aria-label="Link URL"
                    value={linkEditor.url}
                    onChange={(event) => setLinkEditor((current) => ({
                      ...current,
                      url: event.target.value,
                      error: "",
                    }))}
                    placeholder="https://example.com"
                  />
                </label>
                <button type="submit">Apply link</button>
                <button type="button" onClick={closeLinkEditor} aria-label="Cancel link">
                  <i className="fas fa-xmark" aria-hidden="true" />
                </button>
                {linkEditor.error && <small role="alert">{linkEditor.error}</small>}
              </form>
            )}
          </>
        )}
        {block.type === "paragraph" && (
          <textarea
            {...textInputFormattingProps}
            aria-label={`Paragraph block ${index + 1}`}
            rows={5}
            value={block.text || ""}
            onChange={(event) => update({ text: event.target.value })}
            placeholder="Tell the story. Select text to format it or add a link."
          />
        )}
        {block.type === "heading" && (
          <div className="blog-editor-heading-block">
            <label>
              <span>Level</span>
              <select
                aria-label={`Heading level for block ${index + 1}`}
                value={block.level === 3 ? 3 : 2}
                onChange={(event) => update({ level: Number(event.target.value) })}
              >
                <option value="2">H2</option>
                <option value="3">H3</option>
              </select>
            </label>
            <input
              {...textInputFormattingProps}
              type="text"
              aria-label={`Heading block ${index + 1}`}
              value={block.text || ""}
              onChange={(event) => update({ text: event.target.value })}
              placeholder="Section heading"
            />
          </div>
        )}
        {block.type === "list" && (
          <div className="blog-editor-list-block">
            <label>
              <input
                type="checkbox"
                checked={Boolean(block.ordered)}
                onChange={(event) => update({ ordered: event.target.checked })}
              />
              <span>Numbered list</span>
            </label>
            <textarea
              {...textInputFormattingProps}
              aria-label={`List block ${index + 1}`}
              rows={5}
              value={(block.items || []).join("\n")}
              onChange={(event) => update({ items: event.target.value.split("\n") })}
              placeholder={"Add one item per line\nSecond helpful point"}
            />
          </div>
        )}
        {block.type === "quote" && (
          <textarea
            {...textInputFormattingProps}
            className="blog-editor-quote-block"
            aria-label={`Quote block ${index + 1}`}
            rows={4}
            value={block.text || ""}
            onChange={(event) => update({ text: event.target.value })}
            placeholder="A memorable observation or useful insurance insight."
          />
        )}
        {block.type === "image" && (
          <div className="blog-editor-image-block">
            <div
              className={`blog-editor-image-block__preview is-width-${block.width || "full"} is-align-${block.align || "center"}`}
            >
              {block.src ? (
                <img src={block.src} alt={block.alt || ""} loading="lazy" decoding="async" />
              ) : (
                <div className="blog-editor-image-block__placeholder">
                  <i className="far fa-image" aria-hidden="true" />
                  <span>Upload an image or enter a URL</span>
                </div>
              )}
            </div>
            <label className={`blog-editor-image-upload${uploadingImage ? " is-uploading" : ""}`}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={uploadImage}
                disabled={uploadingImage || locked}
              />
              <i className={`fas fa-${uploadingImage ? "spinner fa-spin" : "cloud-arrow-up"}`} aria-hidden="true" />
              <span>{uploadingImage ? "Uploading image..." : "Upload from device"}</span>
              <small>JPEG, PNG, WebP or GIF. Maximum 5 MiB.</small>
            </label>
            {imageUploadError && <small className="blog-editor-image-upload__error" role="alert">{imageUploadError}</small>}
            <label>
              <span>Image URL</span>
              <input
                type="url"
                aria-label={`Image URL for block ${index + 1}`}
                value={block.src || ""}
                onChange={(event) => update({ src: event.target.value })}
                placeholder="https://example.com/destination.jpg"
              />
            </label>
            <div>
              <label>
                <span>Alternative text</span>
                <input
                  id={`blog-editor-image-alt-${block.id}`}
                  type="text"
                  aria-label={`Alternative text for image block ${index + 1}`}
                  value={block.alt || ""}
                  onChange={(event) => update({ alt: event.target.value })}
                  placeholder="Describe the image"
                />
              </label>
              <label>
                <span>Caption</span>
                <input
                  type="text"
                  aria-label={`Caption for image block ${index + 1}`}
                  value={block.caption || ""}
                  onChange={(event) => update({ caption: event.target.value })}
                  placeholder="Optional context or credit"
                />
              </label>
            </div>
            <fieldset className="blog-editor-image-size">
              <legend>Image size</legend>
              {BLOG_IMAGE_WIDTHS.map((size) => (
                <button
                  type="button"
                  key={size.value}
                  className={(block.width || "full") === size.value ? "is-active" : ""}
                  onClick={() => update({ width: size.value })}
                  aria-pressed={(block.width || "full") === size.value}
                >
                  {size.label}
                </button>
              ))}
            </fieldset>
            <fieldset className="blog-editor-image-align">
              <legend>Image alignment</legend>
              {BLOG_IMAGE_ALIGNMENTS.map((alignment) => (
                <button
                  type="button"
                  key={alignment.value}
                  className={(block.align || "center") === alignment.value ? "is-active" : ""}
                  onClick={() => update({ align: alignment.value })}
                  aria-label={alignment.label}
                  aria-pressed={(block.align || "center") === alignment.value}
                >
                  <i className={`fas fa-${alignment.icon}`} aria-hidden="true" />
                </button>
              ))}
            </fieldset>
          </div>
        )}
        {block.type === "video" && (
          <div className="blog-editor-video-block">
            {videoEmbedUrl(block.url) ? (
              <div className="blog-editor-video-block__preview">
                <iframe
                  src={videoEmbedUrl(block.url)}
                  title={block.caption ? `Video preview: ${block.caption}` : "Video preview"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                />
              </div>
            ) : (
              <div className="blog-editor-video-block__placeholder">
                <i className="far fa-circle-play" aria-hidden="true" />
                <span>Paste a YouTube or Vimeo link to preview it.</span>
              </div>
            )}
            <label>
              <span>Video URL</span>
              <input
                type="url"
                aria-label={`Video URL for block ${index + 1}`}
                value={block.url || ""}
                onChange={(event) => update({ url: event.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </label>
            {block.url && !videoEmbedUrl(block.url) && (
              <small className="blog-editor-video-block__error" role="alert">
                Use a complete HTTPS YouTube or Vimeo video link.
              </small>
            )}
            <label>
              <span>Caption</span>
              <input
                type="text"
                aria-label={`Caption for video block ${index + 1}`}
                value={block.caption || ""}
                onChange={(event) => update({ caption: event.target.value })}
                placeholder="Optional video caption"
              />
            </label>
          </div>
        )}
        {block.type === "divider" && (
          <div className="blog-editor-divider-block" aria-label="Section divider">
            <span /><i className="fas fa-plane" aria-hidden="true" /><span />
          </div>
        )}
      </div>
    </section>
  );
};

const PostSettings = ({
  form,
  mode,
  token,
  onFieldChange,
  onModeChange,
  onNotice,
  onUploadState,
}) => {
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState("");

  const uploadCover = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const fileError = imageUploadValidationMessage(file);
    if (fileError) {
      setCoverUploadError(fileError);
      return;
    }

    setCoverUploadError("");
    setCoverUploading(true);
    onUploadState?.(true);
    try {
      const uploaded = await contentApi.uploadImage(file, token);
      onFieldChange("cover_image_url", uploaded.url);
      onNotice?.({ type: "success", message: `${file.name} was uploaded as the cover image.` });
    } catch (error) {
      const message = error?.status === 401
        ? "Your session expired. Sign in again before uploading an image."
        : error?.status === 403
          ? "This account cannot upload blog images."
          : error?.message || "The cover image could not be uploaded.";
      setCoverUploadError(message);
      onNotice?.({ type: "error", message });
    } finally {
      setCoverUploading(false);
      onUploadState?.(false);
    }
  };

  return (
  <div className="blog-editor-settings__content">
    <section className="blog-editor-settings-card">
      <div className="blog-editor-settings-card__heading">
        <span aria-hidden="true"><i className="fas fa-paper-plane" /></span>
        <div><strong>Publishing</strong><small>Control when readers can see this post.</small></div>
      </div>
      <div className="blog-editor-publishing-options">
        {PUBLISHING_OPTIONS.map((option) => (
          <label className={mode === option.value ? "is-selected" : ""} key={option.value}>
            <input
              type="radio"
              name="blog-editor-publishing-mode"
              value={option.value}
              checked={mode === option.value}
              onChange={(event) => onModeChange(event.target.value)}
            />
            <i className={`fas fa-${option.icon}`} aria-hidden="true" />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {mode === "schedule" && (
        <label className="blog-editor-field">
          <span>Publication date and time</span>
          <input
            id="blog-editor-publication-date"
            type="datetime-local"
            aria-label="Publication date and time"
            value={form.published_at}
            min={toDateTimeLocal(new Date())}
            onChange={(event) => onFieldChange("published_at", event.target.value)}
          />
          <small>Entered in your device's local time zone.</small>
        </label>
      )}
    </section>

    <section className="blog-editor-settings-card">
      <div className="blog-editor-settings-card__heading">
        <span aria-hidden="true"><i className="fas fa-tags" /></span>
        <div><strong>Organisation</strong><small>Help readers discover this story.</small></div>
      </div>
      <label className="blog-editor-field">
        <span>Category</span>
        <input
          id="blog-editor-category"
          type="text"
          maxLength={80}
          value={form.category}
          onChange={(event) => onFieldChange("category", event.target.value)}
          placeholder="Insurance Guides"
        />
      </label>
      <label className="blog-editor-field">
        <span>Tags</span>
        <input
          id="blog-editor-tags"
          type="text"
          value={form.tags}
          onChange={(event) => onFieldChange("tags", event.target.value)}
          placeholder="Lagos, Visa tips, Planning"
        />
        <small>Separate up to 12 tags with commas.</small>
      </label>
      <label className="blog-editor-feature-toggle">
        <input
          type="checkbox"
          checked={form.is_featured}
          onChange={(event) => onFieldChange("is_featured", event.target.checked)}
        />
        <span aria-hidden="true" />
        <div><strong>Featured story</strong><small>Give this post priority on the blog landing page.</small></div>
      </label>
    </section>

    <section className="blog-editor-settings-card">
      <div className="blog-editor-settings-card__heading">
        <span aria-hidden="true"><i className="far fa-image" /></span>
        <div><strong>Cover image</strong><small>The lead image used on cards and the article.</small></div>
      </div>
      {form.cover_image_url && (
        <img
          className="blog-editor-cover-preview"
          src={form.cover_image_url}
          alt=""
          loading="lazy"
          decoding="async"
        />
      )}
      <label className={`blog-editor-image-upload blog-editor-image-upload--cover${coverUploading ? " is-uploading" : ""}`}>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={uploadCover}
          disabled={coverUploading}
        />
        <i className={`fas fa-${coverUploading ? "spinner fa-spin" : "cloud-arrow-up"}`} aria-hidden="true" />
        <span>{coverUploading ? "Uploading cover..." : "Upload cover image"}</span>
        <small>JPEG, PNG, WebP or GIF. Maximum 5 MiB.</small>
      </label>
      {coverUploadError && <small className="blog-editor-image-upload__error" role="alert">{coverUploadError}</small>}
      <label className="blog-editor-field">
        <span>Image URL</span>
        <input
          id="blog-editor-cover-image-url"
          type="url"
          maxLength={1000}
          value={form.cover_image_url}
          onChange={(event) => onFieldChange("cover_image_url", event.target.value)}
          placeholder="https://example.com/cover.jpg"
        />
      </label>
      <label className="blog-editor-field">
        <span>Alternative text</span>
        <input
          id="blog-editor-cover-image-alt"
          type="text"
          maxLength={180}
          value={form.cover_image_alt}
          onChange={(event) => onFieldChange("cover_image_alt", event.target.value)}
          placeholder="Describe what is visible"
        />
        <CharacterCount value={form.cover_image_alt} max={180} />
      </label>
      <label className="blog-editor-field">
        <span>Caption</span>
        <input
          type="text"
          maxLength={240}
          value={form.cover_image_caption}
          onChange={(event) => onFieldChange("cover_image_caption", event.target.value)}
          placeholder="Optional context or credit"
        />
      </label>
    </section>

    <section className="blog-editor-settings-card">
      <div className="blog-editor-settings-card__heading">
        <span aria-hidden="true"><i className="fas fa-magnifying-glass" /></span>
        <div><strong>Search preview</strong><small>Optional metadata for search and sharing.</small></div>
      </div>
      <label className="blog-editor-field">
        <span>URL slug</span>
        <input
          type="text"
          maxLength={220}
          value={form.slug}
          onChange={(event) => onFieldChange("slug", event.target.value)}
          placeholder="generated-from-the-title"
        />
        <small>Leave blank on a new post to generate it from the title.</small>
      </label>
      <label className="blog-editor-field">
        <span>SEO title</span>
        <input
          type="text"
          maxLength={70}
          value={form.seo_title}
          onChange={(event) => onFieldChange("seo_title", event.target.value)}
          placeholder="Optional search result title"
        />
        <CharacterCount value={form.seo_title} max={70} />
      </label>
      <label className="blog-editor-field">
        <span>Meta description</span>
        <textarea
          rows={4}
          maxLength={160}
          value={form.meta_description}
          onChange={(event) => onFieldChange("meta_description", event.target.value)}
          placeholder="Optional search and sharing summary"
        />
        <CharacterCount value={form.meta_description} max={160} />
      </label>
    </section>
  </div>
  );
};

const BlockSettings = ({ block, index, total, onMove, onRemove }) => {
  if (!block) {
    return (
      <div className="blog-editor-block-settings-empty">
        <i className="fas fa-cubes-stacked" aria-hidden="true" />
        <strong>Select a content block</strong>
        <span>Its position and quick actions will appear here.</span>
      </div>
    );
  }
  const type = BLOCK_TYPES.find((item) => item.type === block.type) || BLOCK_TYPES[0];
  return (
    <div className="blog-editor-settings__content">
      <section className="blog-editor-settings-card">
        <div className="blog-editor-selected-block">
          <span aria-hidden="true"><i className={`fas fa-${type.icon}`} /></span>
          <div><small>Selected block</small><strong>{type.label}</strong></div>
        </div>
        <p>{type.hint}. Use the controls below to position it in the story.</p>
        <div className="blog-editor-selected-block__actions">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0}>
            <i className="fas fa-arrow-up" aria-hidden="true" /> Move up
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}>
            <i className="fas fa-arrow-down" aria-hidden="true" /> Move down
          </button>
          <button type="button" className="is-danger" onClick={onRemove}>
            <i className="far fa-trash-can" aria-hidden="true" /> Delete block
          </button>
        </div>
      </section>
      <div className="blog-editor-markdown-tip">
        <i className="fas fa-wand-magic-sparkles" aria-hidden="true" />
        <div><strong>Formatting tip</strong><span>Select text in a block, then use the toolbar for bold, italic, underline, links, and more.</span></div>
      </div>
    </div>
  );
};

const ArticlePreview = ({ form, content, mode, authorName }) => (
  <div className="blog-editor-preview" aria-label="Article preview">
    <div className="blog-editor-preview__browser">
      <span /><span /><span />
      <strong>thrive.ng/blog/{form.slug || "your-story"}</strong>
    </div>
    <article>
      <header>
        <span className="blog-editor-preview__category">{form.category || "Insurance guidance"}</span>
        <h1>{form.title || "Your story title"}</h1>
        <p>{form.excerpt || "Your concise article introduction will appear here."}</p>
        <div>
          <span>{authorName || "Thrive Insurance Editorial Team"}</span>
          <span aria-hidden="true">•</span>
          <span>{mode === "schedule" ? "Scheduled preview" : mode === "draft" ? "Draft preview" : "Article preview"}</span>
        </div>
      </header>
      {form.cover_image_url ? (
        <figure className="blog-editor-preview__cover">
          <img src={form.cover_image_url} alt={form.cover_image_alt || ""} />
          {form.cover_image_caption && <figcaption>{form.cover_image_caption}</figcaption>}
        </figure>
      ) : (
        <div className="blog-editor-preview__cover-placeholder">
          <i className="far fa-image" aria-hidden="true" /><span>Cover image</span>
        </div>
      )}
      {content ? (
        <ArticleContent content={content} className="blog-editor-preview__content" />
      ) : (
        <p className="blog-editor-preview__empty">Add content blocks to build the article preview.</p>
      )}
    </article>
  </div>
);

const BlogEditor = ({ token }) => {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const editing = Boolean(postId);
  const titleRef = useRef(null);
  const noticeRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const editRevisionRef = useRef(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [blocks, setBlocks] = useState(() => [createBlock("paragraph")]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [publishingMode, setPublishingMode] = useState("draft");
  const [settingsTab, setSettingsTab] = useState("post");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveIntent, setSaveIntent] = useState(null);
  const [reorderAnnouncement, setReorderAnnouncement] = useState("");
  const [activeUploads, setActiveUploads] = useState(0);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState(null);
  const [post, setPost] = useState(null);
  const [loadState, setLoadState] = useState(editing ? "loading" : "ready");

  const markdown = useMemo(() => blocksToMarkdown(blocks), [blocks]);
  const selectedIndex = blocks.findIndex((block) => block.id === selectedBlockId);
  const selectedBlock = selectedIndex >= 0 ? blocks[selectedIndex] : null;

  useEffect(() => {
    if (!editing) {
      setForm({ ...EMPTY_FORM });
      const initialBlock = createBlock("paragraph");
      setBlocks([initialBlock]);
      setSelectedBlockId(initialBlock.id);
      setPublishingMode("draft");
      setPost(null);
      setLoadState("ready");
      setDirty(false);
      const focusTitle = window.requestAnimationFrame(() => titleRef.current?.focus());
      return () => window.cancelAnimationFrame(focusTitle);
    }

    let current = true;
    setLoadState("loading");
    setNotice(location.state?.editorNotice || null);
    contentApi.getPost(postId, token)
      .then((loadedPost) => {
        if (!current) return;
        const loadedBlocks = markdownToBlocks(loadedPost?.content || "");
        const safeBlocks = loadedBlocks.length ? loadedBlocks : [createBlock("paragraph")];
        setPost(loadedPost);
        setForm(formForPost(loadedPost));
        setBlocks(safeBlocks);
        setSelectedBlockId(safeBlocks[0]?.id || null);
        setPublishingMode(publishingModeFor(loadedPost));
        setDirty(false);
        setLoadState("ready");
      })
      .catch((error) => {
        if (!current) return;
        setNotice({ type: "error", message: error?.message || "This blog post could not be loaded." });
        setLoadState("error");
      });
    return () => {
      current = false;
    };
  }, [editing, location.state, postId, reload, token]);

  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirty]);

  const updateField = (field, value) => {
    editRevisionRef.current += 1;
    setForm((current) => ({ ...current, [field]: value }));
    setDirty(true);
    if (notice?.type === "error") setNotice(null);
  };

  const updateBlock = (blockId, changes) => {
    editRevisionRef.current += 1;
    setBlocks((current) => current.map((block) => (
      block.id === blockId ? { ...block, ...changes } : block
    )));
    setDirty(true);
    if (notice?.type === "error" && notice?.field === "blog-editor-content") {
      setNotice(null);
    }
  };

  const addBlock = (type, afterIndex = blocks.length - 1) => {
    editRevisionRef.current += 1;
    const nextBlock = createBlock(type);
    setBlocks((current) => {
      const insertAt = Math.min(Math.max(afterIndex + 1, 0), current.length);
      return [...current.slice(0, insertAt), nextBlock, ...current.slice(insertAt)];
    });
    setSelectedBlockId(nextBlock.id);
    setSettingsTab("block");
    setDirty(true);
  };

  const moveBlock = (index, direction) => {
    if (activeUploads > 0 || saving) return;
    const target = index + direction;
    if (index < 0 || target < 0 || target >= blocks.length) return;
    editRevisionRef.current += 1;
    setBlocks((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
    const moved = blocks[index];
    if (moved) {
      const type = BLOCK_TYPES.find((item) => item.type === moved.type)?.label || "Block";
      setReorderAnnouncement(`${type} moved to position ${target + 1} of ${blocks.length}.`);
    }
  };

  const moveBlockTo = (draggedId, targetId) => {
    if (activeUploads > 0 || saving) return;
    if (!draggedId || !targetId || draggedId === targetId) return;
    const fromIndex = blocks.findIndex((block) => block.id === draggedId);
    const targetIndex = blocks.findIndex((block) => block.id === targetId);
    if (fromIndex < 0 || targetIndex < 0 || fromIndex === targetIndex) return;
    editRevisionRef.current += 1;
    const next = [...blocks];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(targetIndex, 0, moved);
    setBlocks(next);
    const type = BLOCK_TYPES.find((item) => item.type === moved.type)?.label || "Block";
    setReorderAnnouncement(`${type} moved to position ${targetIndex + 1} of ${next.length}.`);
    setDirty(true);
  };

  const removeBlock = (index) => {
    if (activeUploads > 0 || saving) return;
    if (index < 0) return;
    editRevisionRef.current += 1;
    const remaining = blocks.filter((_, itemIndex) => itemIndex !== index);
    const nextBlocks = remaining.length ? remaining : [createBlock("paragraph")];
    setBlocks(nextBlocks);
    setSelectedBlockId(nextBlocks[Math.min(index, nextBlocks.length - 1)].id);
    setDirty(true);
  };

  const save = async (requestedMode = publishingMode) => {
    if (saveInFlightRef.current) return;
    if (activeUploads > 0) {
      setNotice({
        type: "error",
        message: "Wait for the current image upload to finish before saving this post.",
      });
      window.requestAnimationFrame(() => noticeRef.current?.focus?.({ preventScroll: true }));
      return;
    }

    const validationError = validateEditor(form, blocks, requestedMode);
    if (validationError) {
      setNotice({
        type: "error",
        message: validationError.message,
        field: validationError.field,
      });
      if (validationError.panel === "post") {
        setSettingsTab("post");
        setMobileSettingsOpen(true);
      } else {
        setPreviewOpen(false);
      }
      window.requestAnimationFrame(() => {
        const field = document.getElementById(validationError.field);
        const target = field || noticeRef.current;
        target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        target?.focus?.({ preventScroll: true });
      });
      return;
    }

    saveInFlightRef.current = true;
    const submittedRevision = editRevisionRef.current;
    setSaving(true);
    setSaveIntent(requestedMode);
    setNotice(null);
    try {
      const payload = payloadFor(form, blocks, requestedMode);
      const savedPost = editing
        ? await contentApi.updatePost(postId, payload, token)
        : await contentApi.createPost(payload, token);
      const savedTitle = savedPost?.title || payload.title;
      const action =
        requestedMode === "schedule"
          ? "scheduled"
          : requestedMode === "now"
            ? "published"
            : requestedMode === "archived"
              ? "archived"
              : "saved as a draft";
      setPost(savedPost || { ...post, ...payload });
      setPublishingMode(requestedMode);
      if (editRevisionRef.current === submittedRevision) setDirty(false);
      const successNotice = { type: "success", message: `${savedTitle} was ${action}.` };
      setNotice(successNotice);
      if (!editing && savedPost?.id) {
        navigate(`/content-admin/blog/${savedPost.id}/edit`, {
          replace: true,
          state: { editorNotice: successNotice },
        });
      }
    } catch (error) {
      const authenticationMessage =
        error?.status === 401
          ? "Your session has expired. Sign in again, then retry saving this post."
          : error?.status === 403
            ? "This account no longer has permission to manage blog posts."
            : null;
      setNotice({
        type: "error",
        message: authenticationMessage || error?.message || "The blog post could not be saved.",
      });
      window.requestAnimationFrame(() => {
        noticeRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
        noticeRef.current?.focus?.({ preventScroll: true });
      });
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
      setSaveIntent(null);
    }
  };

  if (loadState === "loading") {
    return (
      <section className="blog-editor-state" role="status" aria-live="polite">
        <span className="travel-admin-gate__spinner" aria-hidden="true" />
        <strong>Opening the editor...</strong>
        <p>Loading the latest saved version of this story.</p>
      </section>
    );
  }

  if (loadState === "error") {
    return (
      <section className="blog-editor-state blog-editor-state--error">
        <i className="fas fa-file-circle-exclamation" aria-hidden="true" />
        <strong>Unable to open this post</strong>
        <p>{notice?.message}</p>
        <div>
          <Link className="travel-admin-button travel-admin-button--secondary" to="/content-admin/blog">Back to posts</Link>
          <button type="button" className="travel-admin-button" onClick={() => setReload((value) => value + 1)}>Try again</button>
        </div>
      </section>
    );
  }

  return (
    <div className="blog-editor-page">
      <header className="blog-editor-topbar">
        <div className="blog-editor-topbar__identity">
          <Link to="/content-admin/blog" aria-label="Back to blog posts">
            <i className="fas fa-arrow-left" aria-hidden="true" />
          </Link>
          <span className="blog-editor-topbar__document-icon" aria-hidden="true"><i className="far fa-file-lines" /></span>
          <div>
            <span>{editing ? "Edit post" : "New post"}</span>
            <strong>{form.title || "Untitled story"}</strong>
          </div>
        </div>
        <div className="blog-editor-topbar__status" aria-live="polite">
          <span className={dirty ? "is-dirty" : "is-saved"} aria-hidden="true" />
          {saving ? "Saving..." : dirty ? "Unsaved changes" : editing ? "All changes saved" : "New draft"}
        </div>
        <div className="blog-editor-topbar__actions">
          <button
            type="button"
            className={`blog-editor-preview-toggle${previewOpen ? " is-active" : ""}`}
            onClick={() => setPreviewOpen((value) => !value)}
            aria-pressed={previewOpen}
          >
            <i className={`far fa-${previewOpen ? "pen-to-square" : "eye"}`} aria-hidden="true" />
            {previewOpen ? "Edit" : "Preview"}
          </button>
          {publishingMode !== "draft" && (
            <button
              type="button"
              className="blog-editor-save-draft"
              onClick={() => save("draft")}
              disabled={saving || activeUploads > 0}
              aria-busy={saving && saveIntent === "draft"}
            >
              <SaveActionIcon saving={saving && saveIntent === "draft"} />
              {saving && saveIntent === "draft" ? "Saving draft..." : "Save draft"}
            </button>
          )}
          <button
            type="button"
            className="travel-admin-button blog-editor-primary-action"
            onClick={() => save()}
            disabled={saving || activeUploads > 0}
            aria-busy={saving && saveIntent === publishingMode}
          >
            <SaveActionIcon saving={saving && saveIntent === publishingMode} />
            {primaryActionLabel(
              publishingMode,
              saving && saveIntent === publishingMode,
              saveIntent
            )}
          </button>
          <button
            type="button"
            className={`blog-editor-settings-toggle${mobileSettingsOpen ? " is-active" : ""}`}
            onClick={() => setMobileSettingsOpen((value) => !value)}
            aria-label="Toggle post settings"
            aria-expanded={mobileSettingsOpen}
          >
            <i className="fas fa-sliders" aria-hidden="true" />
          </button>
        </div>
      </header>

      {notice && (
        <div ref={noticeRef} tabIndex={-1} className="blog-editor-notice-anchor">
          <EditorNotice notice={notice} onDismiss={() => setNotice(null)} />
        </div>
      )}
      <p className="blog-editor-sr-status" role="status" aria-live="polite">{reorderAnnouncement}</p>

      <div className={`blog-editor-workspace${previewOpen ? " is-previewing" : ""}`}>
        {!previewOpen && <BlockInserter onInsert={(type) => addBlock(type)} />}

        <div className="blog-editor-canvas">
          {previewOpen ? (
            <ArticlePreview
              form={form}
              content={markdown}
              mode={publishingMode}
              authorName={post?.author_name}
            />
          ) : (
            <div className="blog-editor-document">
              <div className="blog-editor-document__intro">
                <label htmlFor="blog-editor-title">Post title</label>
                <textarea
                  ref={titleRef}
                  id="blog-editor-title"
                  rows={2}
                  maxLength={200}
                  value={form.title}
                  onChange={(event) => updateField("title", event.target.value)}
                  placeholder="Add a compelling title"
                />
                <CharacterCount value={form.title} max={200} />
                <label htmlFor="blog-editor-excerpt">Excerpt</label>
                <textarea
                  id="blog-editor-excerpt"
                  rows={3}
                  maxLength={360}
                  value={form.excerpt}
                  onChange={(event) => updateField("excerpt", event.target.value)}
                  placeholder="Write a concise summary for article cards and search results."
                />
                <CharacterCount value={form.excerpt} max={360} />
              </div>

              <div className="blog-editor-document__divider">
                <span>Article blocks</span>
                <small>{blocks.length} {blocks.length === 1 ? "block" : "blocks"}</small>
              </div>

              <div id="blog-editor-content" className="blog-editor-block-list" tabIndex={-1}>
                {blocks.map((block, index) => (
                  <React.Fragment key={block.id}>
                    <EditableBlock
                      block={block}
                      index={index}
                      total={blocks.length}
                      selected={block.id === selectedBlockId}
                      locked={saving || activeUploads > 0}
                      token={token}
                      onSelect={() => {
                        setSelectedBlockId(block.id);
                        setSettingsTab("block");
                      }}
                      onChange={updateBlock}
                      onUploaded={(blockId, uploaded) => {
                        editRevisionRef.current += 1;
                        setBlocks((current) => current.map((item) => (
                          item.id === blockId
                            ? {
                                ...item,
                                src: uploaded.url,
                                uploadWidth: uploaded.width,
                                uploadHeight: uploaded.height,
                              }
                            : item
                        )));
                        setDirty(true);
                      }}
                      onMove={(direction) => moveBlock(index, direction)}
                      onMoveTo={moveBlockTo}
                      onRemove={() => removeBlock(index)}
                      onStatus={setNotice}
                      onUploadState={(uploading) => setActiveUploads((count) => (
                        Math.max(0, count + (uploading ? 1 : -1))
                      ))}
                    />
                    <button
                      type="button"
                      className="blog-editor-inline-add"
                      onClick={() => addBlock("paragraph", index)}
                      aria-label={`Add paragraph after block ${index + 1}`}
                    >
                      <i className="fas fa-plus" aria-hidden="true" />
                    </button>
                  </React.Fragment>
                ))}
              </div>

              <div className="blog-editor-end-inserter">
                <span>Continue the story</span>
                <div>
                  {BLOCK_TYPES.map((item) => (
                    <button type="button" key={item.type} onClick={() => addBlock(item.type)}>
                      <i className={`fas fa-${item.icon}`} aria-hidden="true" /> {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <aside className={`blog-editor-settings${mobileSettingsOpen ? " is-open" : ""}`} aria-label="Editor settings">
          <div className="blog-editor-settings__tabs" role="tablist" aria-label="Editor settings panels">
            <button
              type="button"
              role="tab"
              aria-selected={settingsTab === "post"}
              className={settingsTab === "post" ? "is-active" : ""}
              onClick={() => setSettingsTab("post")}
            >Post</button>
            <button
              type="button"
              role="tab"
              aria-selected={settingsTab === "block"}
              className={settingsTab === "block" ? "is-active" : ""}
              onClick={() => setSettingsTab("block")}
            >Block</button>
            <button type="button" className="blog-editor-settings__close" onClick={() => setMobileSettingsOpen(false)} aria-label="Close settings">
              <i className="fas fa-xmark" aria-hidden="true" />
            </button>
          </div>
          {settingsTab === "post" ? (
            <PostSettings
              form={form}
              mode={publishingMode}
              token={token}
              onFieldChange={updateField}
              onModeChange={(mode) => {
                editRevisionRef.current += 1;
                setPublishingMode(mode);
                setDirty(true);
              }}
              onNotice={setNotice}
              onUploadState={(uploading) => setActiveUploads((count) => (
                Math.max(0, count + (uploading ? 1 : -1))
              ))}
            />
          ) : (
            <BlockSettings
              block={selectedBlock}
              index={selectedIndex}
              total={blocks.length}
              onMove={(direction) => moveBlock(selectedIndex, direction)}
              onRemove={() => removeBlock(selectedIndex)}
            />
          )}
        </aside>
      </div>
    </div>
  );
};

export default BlogEditor;
